import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { take } from 'rxjs/operators';
import { AppConstants, decodeJwt } from '@ups/xplat/utils';

/**
 * @description
 * Will allow just authenticathed users to enter.
 * If user is not authenticated, flow will redirsct to SSO login.
 *
 * Note: Only for Auth0 right now...
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  auth = inject(AuthService);
  private _authCallbackInit = false;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | boolean
    | UrlTree
    | Promise<boolean | UrlTree>
    | Observable<boolean | UrlTree> {
    return new Promise((resolve) => {
      // Fast path: right after native Auth0 login the in-memory token is set before
      // isAuthenticated$ refresh queues settle (common after logout → login).
      const token = AppConstants.userToken;
      if (token && !isJwtExpired(token)) {
        resolve(true);
        return;
      }

      this.auth.isAuthenticated$.pipe(take(1)).subscribe((loggedIn) => {
        if (loggedIn) {
          resolve(true);
        } else {
          this.auth
            .loginWithRedirect({
              // eslint-disable-next-line @typescript-eslint/naming-convention
              redirect_uri: state.url,
            })
            .pipe(take(1))
            .subscribe(() => {
              this.auth.isAuthenticated$.pipe(take(1)).subscribe((auth) => {
                if (auth) {
                  resolve(true);
                } else {
                  resolve(false);
                }
              });
            });
        }
      });
    });
  }
}

function isJwtExpired(token: string): boolean {
  const decoded = decodeJwt(token);
  const exp = decoded?.exp;
  if (typeof exp !== 'number') {
    return false;
  }
  return exp < Date.now() / 1000;
}
